import { CheckCircle2, Inbox, QrCode, Sparkles } from "lucide-react";
import { CT } from "@/lib/connectionsTheme";

const StatCard = ({ label, value, testId }) => (
  <div className={`${CT.panel} p-5`} data-testid={testId}>
    <p className={CT.label}>{label}</p>
    <p className="mt-2 text-2xl font-extrabold text-slate-900 dark:text-white">{value}</p>
  </div>
);

export const IntakeOverviewTab = ({ overview, onGoToModels }) => {
  if (!overview) return null;
  const { front_desk_enabled, total_models, active_models, total_qr_codes, total_submissions, readiness } = overview;

  const steps = [
    { done: readiness.model_created, label: "Create an Intake Model" },
    { done: readiness.model_active, label: "Activate an Intake Model" },
    { done: readiness.qr_generated, label: "Generate a QR Code" },
  ];
  const completedCount = steps.filter((s) => s.done).length;
  const progressPct = Math.round((completedCount / steps.length) * 100);

  return (
    <div className="space-y-5" data-testid="intake-overview-tab">
      {!front_desk_enabled && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400" data-testid="intake-overview-frontdesk-warning">
          The Front Desk solution must be enabled for your organization before Customer Intake can accept submissions.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Intake Models" value={total_models} testId="intake-overview-stat-models" />
        <StatCard label="Active Models" value={active_models} testId="intake-overview-stat-active-models" />
        <StatCard label="QR Codes" value={total_qr_codes} testId="intake-overview-stat-qr" />
        <StatCard label="Submissions" value={total_submissions} testId="intake-overview-stat-submissions" />
      </div>

      <div className={`${CT.panel} p-5`} data-testid="intake-overview-readiness">
        <div className="flex items-center justify-between">
          <p className={CT.cardTitle}>Intake Readiness</p>
          <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{completedCount} / {steps.length} complete</span>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/5" data-testid="intake-readiness-progress-bar">
          <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
          {steps.map((s, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`intake-readiness-step-${idx}`}>
              <CheckCircle2 size={14} className={s.done ? "text-emerald-500 dark:text-emerald-400" : "text-slate-300 dark:text-slate-600"} />
              <span className={`text-xs font-semibold ${s.done ? "text-slate-700 dark:text-slate-200" : "text-slate-400 dark:text-slate-500"}`}>{s.label}</span>
            </div>
          ))}
        </div>
        <button type="button" onClick={onGoToModels} data-testid="intake-overview-go-to-models-btn" className={`${CT.btnPrimary} mt-5`}>
          <Sparkles size={14} /> {total_models === 0 ? "Create Your First Intake Model" : "Manage Intake Models"}
        </button>
      </div>

      {total_models === 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-slate-200 p-5 text-sm text-slate-500 dark:border-white/15 dark:text-slate-400" data-testid="intake-overview-empty-hint">
          <Inbox size={18} className="text-slate-400 dark:text-slate-500" />
          Intake Models let you define what information customers provide and how they verify their identity before submitting a request.
        </div>
      )}
      {total_models > 0 && total_qr_codes === 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-slate-200 p-5 text-sm text-slate-500 dark:border-white/15 dark:text-slate-400" data-testid="intake-overview-qr-hint">
          <QrCode size={18} className="text-slate-400 dark:text-slate-500" />
          Once a model is active, generate QR codes under the QR Codes tab to distribute it.
        </div>
      )}
    </div>
  );
};
